"use client"

import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Navbar } from "@/components/layout/navbar"
import { Button } from "@/components/ui/button"
import { Check, Info, Loader2, X } from "lucide-react"
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useQuery } from "@tanstack/react-query"
import { subscriptionPaymentService } from "@/services/subscriptionPaymentService"
import { BillingCycleToggle } from "@/components/pricing/BillingCycleToggle"
import { useAuth } from "@/hooks/use-auth"

export default function PricingPage() {
    const [billingCycle, setBillingCycle] = useState<number>(3) // Default to Quarterly (3 months)

    const { data: plans, isLoading } = useQuery({
        queryKey: ["subscription-plans"],
        queryFn: subscriptionPaymentService.getPlans,
    })

    const { user } = useAuth()
    const currentPlanName = user?.active_plan?.toLowerCase() ?? null

    const router = useRouter()
    const handlePurchase = (productKey: string) => {
        router.push(`/checkout?product_key=${productKey}&months=${billingCycle}`)
    }

    const displayPlans = plans ?? []

    return (
        <div className="flex min-h-screen flex-col">
            <Navbar />

            <main className="flex-1 bg-muted/30">
                <div className="container pt-16 pb-8 px-4 mx-auto text-center space-y-6">
                    <div className="space-y-4">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                            Choose your <span className="text-primary">Mastery</span> Plan
                        </h1>

                    </div>

                    {/* Billing Cycle Toggle */}
                    <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />
                </div>

                <div className="container px-4 mx-auto pb-24">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {/* Dynamic Plans */}
                        {isLoading ? (
                            <div className="col-span-3 flex justify-center items-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            [...displayPlans]
                                .sort((a, b) => a.order_index - b.order_index)
                                .map((plan) => {
                                    const periodEntries = Object.entries(plan.periods ?? {})
                                    const positivePeriodEntries = periodEntries.filter(([, pricing]) => (pricing?.BDT ?? 0) > 0)
                                    const hasPaidPeriods = positivePeriodEntries.length > 0

                                    // Some plans do not define a 1-month price. Use the cheapest available
                                    // monthly equivalent as the base for display/fallback math.
                                    const inferredMonthlyBase = hasPaidPeriods
                                        ? Math.min(
                                            ...positivePeriodEntries.map(([months, pricing]) => {
                                                const parsedMonths = Number(months)
                                                const safeMonths = Number.isFinite(parsedMonths) && parsedMonths > 0 ? parsedMonths : 1
                                                return (pricing.BDT ?? 0) / safeMonths
                                            }),
                                        )
                                        : 0

                                    const baseBDT = plan.periods?.["1"]?.BDT ?? inferredMonthlyBase
                                    const isFree = !hasPaidPeriods
                                    const isBestValue = plan.is_best_value
                                    const isHighlight = plan.is_popular

                                    // Price for the selected billing cycle
                                    const priceDisplay = plan.periods?.[String(billingCycle)]?.BDT ?? baseBDT * billingCycle
                                    const originalPrice = baseBDT * billingCycle
                                    const savings = Math.max(0, originalPrice - priceDisplay)
                                    const perMonthPrice = billingCycle > 1 ? Math.round(priceDisplay / billingCycle) : baseBDT

                                    return (
                                        <Card
                                            key={plan.product_key}
                                            className={`flex flex-col h-full border-2 relative transition-all duration-300 hover:shadow-xl ${isHighlight
                                                ? "border-primary shadow-lg z-10"
                                                : isBestValue
                                                    ? "border-amber-400 shadow-lg hover:border-amber-400/80"
                                                    : "border-border hover:border-primary/50"
                                                }`}
                                        >
                                            {isHighlight && (
                                                <div className="absolute -top-4 left-0 right-0 mx-auto w-fit bg-primary text-primary-foreground text-sm font-bold px-4 py-1 rounded-full text-center shadow-md uppercase tracking-wider">
                                                    Most Popular
                                                </div>
                                            )}
                                            {isBestValue && (
                                                <div className="absolute -top-4 left-0 right-0 mx-auto w-fit bg-amber-500 text-white text-sm font-bold px-4 py-1 rounded-full text-center shadow-md uppercase tracking-wider">
                                                    Best Value
                                                </div>
                                            )}

                                            <CardHeader className="pt-6 pb-2 text-center">
                                                <CardTitle className="text-3xl font-black tracking-tighter uppercase">
                                                    {plan.name}
                                                </CardTitle>
                                                {plan.description && (
                                                    <CardDescription className="font-medium text-foreground/80 mt-1">
                                                        {plan.description}
                                                    </CardDescription>
                                                )}
                                            </CardHeader>

                                            <CardContent className="flex-1 space-y-4">
                                                <div className="flex flex-col items-center justify-center p-3 bg-muted/50 rounded-xl border border-border/50">
                                                    {isFree ? (
                                                        <span className="text-4xl font-extrabold tracking-tight">Free</span>
                                                    ) : (
                                                        <>
                                                            <div className="flex items-baseline justify-center gap-1">
                                                                <span className="text-4xl font-extrabold tracking-tight">
                                                                    ৳{perMonthPrice}
                                                                </span>
                                                                <span className="text-muted-foreground text-sm font-medium">/mo</span>
                                                            </div>
                                                            {billingCycle > 1 && savings > 0 && (
                                                                <div className="mt-1 text-sm font-medium text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                                                                    Save ৳{savings}
                                                                </div>
                                                            )}
                                                            {billingCycle > 1 && (
                                                                <div className="mt-1 text-sm text-muted-foreground">
                                                                    Total ৳{priceDisplay} /{billingCycle}mo
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>

                                                <ul className="space-y-3 text-left">
                                                    {(plan.features ?? []).map((feature, i) => (
                                                        <li key={i} className={`flex items-start gap-2 text-sm ${!feature.included ? "text-muted-foreground/60" : ""}`}>
                                                            {feature.included ? (
                                                                <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                                            ) : (
                                                                <X className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                                            )}
                                                            <span className={!feature.included ? "line-through decoration-muted-foreground/30" : ""}>
                                                                {feature.name ?? feature.text}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </CardContent>

                                            <CardFooter className="pt-2 pb-5">
                                                {currentPlanName && currentPlanName === plan.name.toLowerCase() ? (
                                                    <Button
                                                        className="w-full font-semibold transition-all py-6 text-base shadow-md bg-muted text-muted-foreground border border-border cursor-default"
                                                        size="lg"
                                                        disabled
                                                    >
                                                        Current Plan
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        className={`w-full font-semibold transition-all py-6 text-base shadow-md ${plan.name.toLowerCase() === 'dedicated'
                                                            ? 'bg-amber-500 hover:bg-amber-600 text-white border-none'
                                                            : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                                                            }`}
                                                        size="lg"
                                                        onClick={() => handlePurchase(plan.product_key)}
                                                    >
                                                        {isFree ? "Start For Free" : `Get ${plan.name}`}
                                                    </Button>
                                                )}
                                            </CardFooter>
                                        </Card>
                                    )
                                })
                        )}

                        {/* If no plans found (e.g. while loading or error) */}
                        {!isLoading && displayPlans.length === 0 && (
                            <div className="col-span-3 text-center py-12 text-muted-foreground">
                                No plans available at the moment.
                            </div>
                        )}
                    </div>


                </div>
            </main>

            <footer className="border-t py-12 bg-muted/30">
                <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
                    &copy; {new Date().getFullYear()} SAT Buddy. All rights reserved.
                </div>
            </footer>
        </div>
    )
}
